/**
 * Feature flags for gradual migration from Chart.js to Recharts
 * 
 * This system allows us to:
 * - Gradually migrate components without breaking existing functionality
 * - Rollback changes if issues are discovered
 * - Test both implementations side by side
 * - Control migration at different levels (component, feature, global)
 */

export interface FeatureFlags {
  /** Enable detailed performance monitoring */
  enablePerformanceMonitoring: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  enablePerformanceMonitoring: true,
};

// Environment-based overrides
const ENV_FLAGS: Partial<FeatureFlags> = {
  // Development environment can enable more features for testing
  ...(process.env.NODE_ENV === 'development' && {
    enablePerformanceMonitoring: true,
  }),
};

// URL parameter overrides for testing
const URL_FLAGS: Partial<FeatureFlags> = (() => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const flags: Partial<FeatureFlags> = {};

  if (urlParams.has('performance')) {
    flags.enablePerformanceMonitoring = urlParams.get('performance') === 'true';
  }

  return flags;
})();

/**
 * Get the current feature flag configuration
 */
export function getFeatureFlags(): FeatureFlags {
  return {
    ...DEFAULT_FLAGS,
    ...ENV_FLAGS,
    ...URL_FLAGS,
  };
}

/**
 * Check if a specific feature flag is enabled
 */
export function isFeatureEnabled(flag: keyof FeatureFlags): boolean {
  const flags = getFeatureFlags();
  return flags[flag];
}

/**
 * Enable a feature flag (for runtime configuration)
 */
export function enableFeature(flag: keyof FeatureFlags): void {
  // This would typically update a global state or localStorage
  // For now, we'll use localStorage for persistence
  const currentFlags = getFeatureFlags();
  localStorage.setItem(`feature_flag_${flag}`, 'true');
}

/**
 * Disable a feature flag (for runtime configuration)
 */
export function disableFeature(flag: keyof FeatureFlags): void {
  localStorage.setItem(`feature_flag_${flag}`, 'false');
}

/**
 * Reset all feature flags to defaults
 */
export function resetFeatureFlags(): void {
  Object.keys(DEFAULT_FLAGS).forEach(flag => {
    localStorage.removeItem(`feature_flag_${flag}`);
  });
}

// Migration status no longer needed after full Recharts migration

/**
 * Performance monitoring wrapper for operations
 */
export function withPerformanceMonitoring<T>(
  operationName: string,
  operation: () => T
): T {
  if (!isFeatureEnabled('enablePerformanceMonitoring')) {
    return operation();
  }

  const startTime = performance.now();
  const result = operation();
  const endTime = performance.now();

  console.log(`⏱️ ${operationName} took ${(endTime - startTime).toFixed(2)}ms`);

  return result;
}

// withChartFallback removed as migration is complete